import { PayloadAction, createSlice } from "@reduxjs/toolkit";

import {
	CustomInputType,
	DefaultValueGetter,
	DefaultValueGettersObject
} from "@remar/shared/dist/components/CustomInput/customInput.model";
import { ExternalIntegrationIds, UserTypes } from "@remar/shared/dist/constants";
import {
	DigitalAssetIntegrationData,
	ShipStationIntegrationData,
	StripeIntegrationData,
	UserSubscriptionTypeExternalIntegrationDataItem
} from "@remar/shared/dist/models";
import {
	BaseSubItemFormInputsRawData,
	CreateFormOptionsInputItem,
	CreateFormOptionsInputsObject
} from "@remar/shared/dist/utils/form/form.model";
import {
	createForm,
	getBaseCreateFormSubItemConfig,
	getPopulateInputsAction,
	parseTemplate,
	validateFormAction as utilsValidateFormAction
} from "@remar/shared/dist/utils/form/form.utils";
import {
	getResetState,
	getStateValue as utilsGetStateValue,
	populateData as utilsPopulateData,
	setStateValue as utilsSetStateValue
} from "@remar/shared/dist/utils/stateUtils";
import { cloneDeep, get, set } from "lodash";
import { AppThunk, RootState } from "store";
import {
	CourseCreateDto,
	CourseUpdateDataDto,
	UserSubscriptionTypeExternalIntegrationDataItemUpsertDto,
	coursesService,
	userSubscriptionTypesService
} from "store/services";
import { v4 as uuid } from "uuid";

import { PAYMENT_CONSTANTS, STATE_BASE_NAME, STRIPE_CURRENCY_SYMBOL_MAP } from "./courseAddEdit.constants";
import {
	CourseAddEditState,
	CourseFeaturesFormInputs,
	CourseFeaturesFormRawData,
	CourseFormInputs,
	CourseFormRawData,
	DefaultCourseItem,
	UserSubscriptionTypesFormAddonInputsItem,
	UserSubscriptionTypesFormAddonInputsItemRawData,
	UserSubscriptionTypesFormInputs,
	UserSubscriptionTypesFormRawData,
	UserSubscriptionTypesFormRawDataItem,
	UserSubscriptionTypesFormTemplates
} from "./models";

import { _emit, emit } from "../notifications/notifications.slice";

const defaultValueGetters: DefaultValueGettersObject = {
	uuidV4: (() => uuid()) as DefaultValueGetter
};

export function getDurationStringFromDuration(duration: { months?: number; days?: number }): string {
	const { days, months } = duration;
	let durationString = "";
	if (months) {
		durationString += `${months} months`;
	}
	if (days) {
		durationString += `${durationString.length ? " " : ""}${days} days`;
	}
	return durationString;
}

export function getDigitalAssetInputsConfig(options: { isRequired: boolean }): {
	[inputName: string]: CreateFormOptionsInputItem<unknown>;
} {
	const { isRequired } = options;
	return {
		...getBaseCreateFormSubItemConfig(),
		isLoading: {
			defaultValue: false,
			type: CustomInputType.Checkbox
		},
		name: { type: CustomInputType.Text },
		productKey: {
			imageFileType: "application/pdf",
			imageExtensionName: "pdf",
			isImageFile: false,
			label: "Upload Digital Asset",
			placeholder: "Upload Digital Asset",
			type: CustomInputType.File,
			uploaderIconName: "cloud-upload",
			uploaderStateLoaderKey: "saveIsLoading",
			validations: { required: isRequired }
		}
	};
}

export function getShipStationInputsConfig(options: { isRequired: boolean }): {
	[inputName: string]: CreateFormOptionsInputItem<unknown>;
} {
	const { isRequired } = options;
	return {
		...getBaseCreateFormSubItemConfig(),
		isLoading: {
			defaultValue: false,
			type: CustomInputType.Checkbox
		},
		name: { type: CustomInputType.Text },
		productSKU: {
			label: "Paste Id Here",
			placeholder: "Paste Id Here",
			type: CustomInputType.Text,
			validations: isRequired ? { required: true } : undefined
		}
	};
}

function getStripeInputsConfig(options: { isRequired: boolean }): {
	[inputName: string]:
		| CreateFormOptionsInputItem<unknown>
		| { [inputName: string]: CreateFormOptionsInputItem<unknown> }[];
} {
	const { isRequired } = options;
	return {
		...getBaseCreateFormSubItemConfig(),
		duration: { type: CustomInputType.Text },
		isLoading: {
			defaultValue: false,
			type: CustomInputType.Checkbox
		},
		name: { type: CustomInputType.Text },
		planId: {
			label: "Paste Link Here",
			placeholder: "Paste Link Here",
			type: CustomInputType.Text,
			validations: isRequired ? { required: true } : undefined
		},
		price: { defaultValue: 0, type: CustomInputType.Number },
		counterpartIds: [getBaseCreateFormSubItemConfig()]
	};
}

// this object must follow the structure of UserSubscriptionTypesFormInputsItem
function getSubscriptionInputsConfig(
	key: string,
	options: {
		isRecurring: boolean;
		isRequired: boolean;
		isTrial: boolean;
		withAddons?: boolean;
		withInitialBook?: boolean;
	}
): CreateFormOptionsInputsObject {
	const { isRecurring, isRequired, isTrial, withAddons, withInitialBook } = options;
	const item: CreateFormOptionsInputsObject = {
		id: { type: CustomInputType.Number },
		draftId: {
			type: CustomInputType.Text,
			useDefaultValueGetterName: "uuidV4"
		},
		nextSubscriptionTypeId: { type: CustomInputType.Number },
		nextSubscriptionTypeDraftId: { type: CustomInputType.Text },
		isRecurring: {
			defaultValue: isRecurring,
			type: CustomInputType.Checkbox
		},
		isDigital: {
			defaultValue: false,
			label: "Digital Asset",
			type: CustomInputType.Checkbox
		},
		isTrial: {
			defaultValue: isTrial,
			type: CustomInputType.Checkbox
		},
		stripeItem: getStripeInputsConfig({ isRequired }),
		addons: []
	};
	if (withInitialBook) {
		item.shipStationItem = getShipStationInputsConfig({ isRequired });
		item.digitalAssetItem = getDigitalAssetInputsConfig({ isRequired: false });
	}
	if (withAddons) {
		item.addons = [
			{
				shipStationItem: getShipStationInputsConfig({ isRequired }),
				digitalAssetItem: getDigitalAssetInputsConfig({ isRequired: false }),
				stripeItem: getStripeInputsConfig({ isRequired }),
				isDigital: {
					defaultValue: false,
					label: "Digital Asset",
					type: CustomInputType.Checkbox
				},
				isDefault: {
					defaultValue: false,
					label: "Selected by default",
					type: CustomInputType.Checkbox
				},
				mainImageKey: {
					imageExtensionName: "png",
					imageFileType: "image/png",
					isImageFile: true,
					label: "Upload  Media",
					placeholder: "Upload  Media",
					type: CustomInputType.File,
					uploaderIconName: "cloud-upload",
					uploaderStateLoaderKey: "uploading"
				},
				mainImageUrl: {
					type: CustomInputType.Text
				}
			}
		];
	}
	return item;
}

export const initialState: CourseAddEditState = {
	canSaveForm: false,
	courseForm: createForm<CourseFormInputs, CourseFormRawData, {}>({
		defaultValueGetters,
		inputs: {
			id: {
				type: CustomInputType.Number
			},
			name: {
				label: "Enter Course Title",
				placeholder: "Enter Course Title",
				type: CustomInputType.Text,
				validations: { required: true, maxLength: 140 }
			},
			endOfCourseSummary: {
				type: CustomInputType.Text
			},
			timeLengthHours: {
				defaultValue: 0,
				label: "Hours",
				placeholder: "Hours",
				type: CustomInputType.Number,
				validations: { isInteger: true, min: 0 }
			},
			timeLengthMinutes: {
				defaultValue: 0,
				label: "Minutes",
				placeholder: "Minutes",
				type: CustomInputType.Number,
				validations: { isInteger: true, min: 0 }
			},
			isActive: {
				defaultValue: false,
				type: CustomInputType.Checkbox
			},
			calculateTimeLengthAutomatically: {
				defaultValue: true,
				label: "Calculate Length Automatically",
				type: CustomInputType.Checkbox
			},
			mainImageKey: {
				imageExtensionName: "png",
				imageFileType: "image/png",
				isImageFile: true,
				label: "Upload Thumbnail",
				placeholder: "Upload Thumbnail",
				type: CustomInputType.File,
				uploaderIconName: "camera",
				uploaderStateLoaderKey: "saveIsLoading"
			},
			mainImageUrl: {
				type: CustomInputType.Text
			}
		},
		statePath: "courseForm"
	}),
	data: {
		...DefaultCourseItem,
		externalIntegrationData: { initial: { isDigital: false, addons: [] }, recurring: {}, trial: {} }
	},
	publishIsLoading: false,
	saveIsLoading: false,
	externalIntegrationData: {
		currency: "",
		planName: "",
		bookName: ""
	},
	userSubscriptionTypesForm: createForm<
		UserSubscriptionTypesFormInputs,
		UserSubscriptionTypesFormRawData,
		UserSubscriptionTypesFormTemplates
	>({
		defaultValueGetters,
		generateTemplatesFromPaths: [
			"initial.stripeItem.counterpartIds",
			"initial.addons",
			"initial.addons.0.stripeItem.counterpartIds",
			"recurring.stripeItem.counterpartIds",
			"trial.stripeItem.counterpartIds"
		],
		inputs: {
			initial: getSubscriptionInputsConfig("initial", {
				isRecurring: false,
				isRequired: true,
				isTrial: false,
				withAddons: true,
				withInitialBook: true
			}),
			recurring: getSubscriptionInputsConfig("recurring", {
				isRecurring: true,
				isRequired: true,
				isTrial: false
			}),
			trial: getSubscriptionInputsConfig("trial", {
				isRecurring: false,
				isRequired: false,
				isTrial: true
			}),
			hasTrial: {
				defaultValue: false,
				label: "Includes Free Trial",
				type: CustomInputType.Checkbox
			}
		},
		statePath: "userSubscriptionTypesForm"
	}),
	courseFeaturesForm: createForm<CourseFeaturesFormInputs, CourseFeaturesFormRawData, {}>({
		defaultValueGetters,
		inputs: {
			feature: {
				placeholder: "Enter Course Feature",
				type: CustomInputType.Text
			},
			additionalInfo: {
				placeholder: "Enter Additional Information",
				type: CustomInputType.Text,
				validations: { required: true, maxLength: 250 }
			}
		},
		statePath: "courseFeaturesForm"
	}),
	isLoading: false
};

const utilsPopulateInputs = getPopulateInputsAction<CourseAddEditState>({ defaultValueGetters });
const utilsResetState = getResetState<CourseAddEditState>(initialState);

export const courseAddEditSlice = createSlice({
	name: STATE_BASE_NAME,
	initialState,
	reducers: {
		createAddonItem: (state, action: PayloadAction<string>) => {
			const addonTemplatePath = `${action.payload}.addons`;
			const addonItemsPath = `userSubscriptionTypesForm.inputs.${addonTemplatePath}`;
			const addons = get(state, addonItemsPath) as UserSubscriptionTypesFormAddonInputsItem[];
			const newAddonItem = parseTemplate(
				state.userSubscriptionTypesForm.templates[addonTemplatePath],
				`${addonItemsPath}.${addons.length}`,
				{ defaultValueGetters }
			) as UserSubscriptionTypesFormAddonInputsItem;
			addons!.push(newAddonItem);
			set(state, addonItemsPath, addons);
		},
		addCourseFeature: (state, action: PayloadAction<string>) => {
			const value = action.payload;
			value && state.data.data.marketingPoints.push(action.payload);
			state.courseFeaturesForm.inputs.feature.value = "";
		},
		deleteCourseFeature: (state, action: PayloadAction<string>) => {
			const value = action.payload;
			state.data.data.marketingPoints = state.data.data.marketingPoints.filter(
				marketingPoint => marketingPoint !== value
			);
		},
		updateCourseFeatures: (state, action: PayloadAction<{ id: number; feature: string }[]>) => {
			const arr = action.payload;
			const filteredArray = arr.filter(item => !item.id);
			state.data.data.marketingPoints = filteredArray as unknown as string[];
		},
		populateData: utilsPopulateData,
		populateInputs: utilsPopulateInputs,
		removeAddonItem: (state, action: PayloadAction<{ addonParent: string; addonIndex: number }>) => {
			const { addonParent, addonIndex } = action.payload;
			const addonItemsPath = `userSubscriptionTypesForm.inputs.${addonParent}.addons.${addonIndex}`;
			set(state, `${addonItemsPath}.shipStationItem.deleted.value`, true);
			set(state, `${addonItemsPath}.stripeItem.deleted.value`, true);
		},
		resetState: utilsResetState,
		resetUserSubscriptionTypesForm: state => {
			const newInitialState = cloneDeep(initialState);
			state.userSubscriptionTypesForm = newInitialState.userSubscriptionTypesForm;
		},
		setStateValue: utilsSetStateValue,
		validateForm: utilsValidateFormAction
	}
});

export const {
	createAddonItem,
	addCourseFeature,
	deleteCourseFeature,
	updateCourseFeatures,
	populateData,
	populateInputs,
	removeAddonItem,
	resetState,
	resetUserSubscriptionTypesForm,
	setStateValue,
	validateForm
} = courseAddEditSlice.actions;

export const fetchData = (id: number, options?: { form?: string; stateLoaderKey?: string }): AppThunk => {
	return async (dispatch, getState) => {
		const { form, stateLoaderKey = "isLoading" } = options || {};
		try {
			dispatch(resetUserSubscriptionTypesForm());
			const { courseAddEdit: state } = getState();
			dispatch(setStateValue({ key: stateLoaderKey, value: true }));
			const courseData = (await coursesService.findOne(id, {
				include: ["allowedForUST.subTypeEIDItems.children.counterparts", "allowedForUST.subTypeEIDItems.counterparts"],
				orderBy: { "allowedForUST.subTypeEIDItems.id": "asc" }
			}))!;
			let stateData = form ? { ...state.data } : {};
			if (!form || form === "courseForm") {
				const totalLength = courseData.calculateTimeLengthAutomatically
					? courseData.manualTimeLengthComponent
					: courseData.totalTimeLengthInMinutes;
				const courseFormInputValues = {
					...courseData,
					timeLengthHours: Math.floor(totalLength / 60),
					timeLengthMinutes: Math.floor(totalLength % 60)
				};
				stateData = { ...stateData, ...courseData };
				dispatch(
					setStateValue({
						key: "data",
						value: stateData
					})
				);
				dispatch(
					populateInputs({
						inputsStatePath: "courseForm.inputs",
						templatesStatePath: "courseForm.templates",
						values: courseFormInputValues as unknown as Record<string, unknown>
					})
				);
				if (courseFormInputValues.mainImageKey && courseFormInputValues.mainImageUrl) {
					dispatch(
						setStateValue({ key: "courseForm.inputs.mainImageKey.imageUrl", value: courseFormInputValues.mainImageUrl })
					);
				}
				dispatch(validateForm({ formStatePath: "courseForm" }));
			}
			if (!form || form === "userSubscriptionTypesForm") {
				const externalIntegrationData = { currency: "", planName: "" };
				const ustFormInputValues = cloneDeep(state.userSubscriptionTypesForm.rawData);
				ustFormInputValues.initial.addons = [];
				ustFormInputValues.hasTrial = courseData.hasTrial as boolean;
				ustFormInputValues.initial.isDigital = false;
				dispatch(
					setStateValue({
						key: "data",
						value: {
							...stateData,
							externalIntegrationData: { initial: { addons: [], isDigital: false }, recurring: {}, trial: {} }
						}
					})
				);
				courseData
					.allowedForUST!.filter(ustItem => ustItem.isActive)
					.forEach(ustItem => {
						const { duration, isRecurring, isTrial } = ustItem;
						const mainStripeIntegrationItem =
							ustItem.subTypeEIDItems!.find(
								item => item.integrationId === ExternalIntegrationIds.Stripe && !item.parentId
							) || ({ data: {} } as UserSubscriptionTypeExternalIntegrationDataItem);
						const { children: dbAddons, counterparts: mainStripeItemCounterparts } = mainStripeIntegrationItem;
						const mainStripeIntegrationData = mainStripeIntegrationItem.data as StripeIntegrationData;
						const { planId: mainPlanId } = mainStripeIntegrationData;
						const commonData: UserSubscriptionTypesFormRawDataItem = {
							...ustItem,
							deleted: false,
							shipStationItem: {
								id: 0,
								draftId: uuid(),
								name: "",
								productSKU: "",
								deleted: false
							},
							stripeItem: {
								id: mainStripeIntegrationItem.id,
								draftId: uuid(),
								counterpartIds: mainStripeItemCounterparts
									? mainStripeItemCounterparts.map(item => {
											return { id: item.id, draftId: "", deleted: false };
									  })
									: [],
								duration: "",
								planId: mainPlanId,
								price: 0,
								name: mainStripeIntegrationData.name!,
								deleted: false
							}
						} as UserSubscriptionTypesFormRawDataItem;
						let ustItemType = "";
						if (!externalIntegrationData.currency) {
							externalIntegrationData.currency = STRIPE_CURRENCY_SYMBOL_MAP[mainStripeIntegrationData.currency!];
						}
						if (!externalIntegrationData.planName) {
							externalIntegrationData.planName = mainStripeIntegrationData.planName!;
						}
						if (duration) {
							commonData.stripeItem.duration = getDurationStringFromDuration(duration);
						}
						if (isTrial) {
							ustItemType = "trial";
						} else if (isRecurring) {
							ustItemType = "recurring";
							ustFormInputValues.initial.nextSubscriptionTypeId = ustItem.id;
							commonData.stripeItem.price = mainStripeIntegrationData.price!;
						} else {
							ustItemType = "initial";
							ustFormInputValues.trial.nextSubscriptionTypeId = ustItem.id;
							const initialShipStationIntegrationItem =
								mainStripeItemCounterparts &&
								mainStripeItemCounterparts.find(item => item.integrationId === ExternalIntegrationIds.ShipStation);
							const initialDigitalAssetIntegrationItem =
								mainStripeItemCounterparts &&
								mainStripeItemCounterparts.find(item => item.integrationId === ExternalIntegrationIds.DigitalAsset);
							ustFormInputValues.initial.isDigital = !!initialDigitalAssetIntegrationItem;
							const addons: UserSubscriptionTypesFormAddonInputsItemRawData[] = [];
							const dataAddons: {
								mainImageUrl?: string;
								mainImageKey?: string;
								stripePlanId: string;
								shipStationProductSKU?: string;
								digitalAssetProductKey?: string;
							}[] = [];
							commonData.stripeItem.price = mainStripeIntegrationData.price!;
							if (initialDigitalAssetIntegrationItem) {
								const digitalAssetData = initialDigitalAssetIntegrationItem.data as DigitalAssetIntegrationData;
								commonData.digitalAssetItem = {
									...commonData.digitalAssetItem,
									id: initialDigitalAssetIntegrationItem.id,
									name: digitalAssetData.name!,
									productKey: digitalAssetData.productKey
								};
								dispatch(
									setStateValue({
										key: "data.externalIntegrationData.initial.digitalAssetProductKey",
										value: digitalAssetData.productKey
									})
								);
								dispatch(
									setStateValue({
										key: "userSubscriptionTypesForm.inputs.initial.digitalAssetItem.productKey.name",
										value: digitalAssetData.name
									})
								);
								dispatch(toggleDigitalAsset(true, "initial"));
							}
							if (initialShipStationIntegrationItem) {
								const shipStationData = initialShipStationIntegrationItem.data as ShipStationIntegrationData;
								commonData.shipStationItem = {
									...commonData.shipStationItem,
									id: initialShipStationIntegrationItem.id,
									name: shipStationData.name!,
									productSKU: shipStationData.productSKU
								};
								dispatch(
									setStateValue({
										key: "data.externalIntegrationData.initial.shipStationProductSKU",
										value: shipStationData.productSKU
									})
								);
								dispatch(toggleDigitalAsset(false, "initial"));
							}
							if (dbAddons instanceof Array && dbAddons.length) {
								dbAddons.forEach(addonItem => {
									if (addonItem.integrationId !== ExternalIntegrationIds.Stripe) {
										return;
									}
									const { counterparts } = addonItem;
									const addonStripeData = addonItem.data as StripeIntegrationData;
									let addonIsDigital = false;
									const counterpartIds: BaseSubItemFormInputsRawData[] = [];
									let addonShipStationItem = {
										deleted: false,
										draftId: uuid(),
										id: 0,
										name: "",
										productSKU: ""
									};
									//TODO:UPDATE FETCH FOR DIGITAL ASSETS
									let addonDigitalAssetItem = {
										deleted: false,
										draftId: uuid(),
										id: 0,
										name: "",
										productKey: ""
									};
									if (counterparts && counterparts.length) {
										const shipStationIntegrationItem = counterparts.find(
											item => item.integrationId === ExternalIntegrationIds.ShipStation
										);
										const digitalAssetIntegrationItem = counterparts.find(
											item => item.integrationId === ExternalIntegrationIds.DigitalAsset
										);
										addonIsDigital = !!digitalAssetIntegrationItem;
										if (shipStationIntegrationItem) {
											const addonShipStationData = shipStationIntegrationItem.data as ShipStationIntegrationData;
											addonShipStationItem = {
												...addonShipStationItem,
												id: shipStationIntegrationItem.id,
												name: addonShipStationData.name!,
												productSKU: addonShipStationData.productSKU
											};
										}
										if (digitalAssetIntegrationItem) {
											const addonShipStationData = digitalAssetIntegrationItem.data as DigitalAssetIntegrationData;
											addonDigitalAssetItem = {
												...addonDigitalAssetItem,
												id: digitalAssetIntegrationItem.id,
												name: addonShipStationData.name!,
												productKey: addonShipStationData.productKey
											};
										}
									}
									if (!addonIsDigital && !addonShipStationItem.id) {
										counterpartIds.push({ id: 0, draftId: addonShipStationItem.draftId, deleted: false });
									}
									if (addonIsDigital && !addonDigitalAssetItem.id) {
										counterpartIds.push({ id: 0, draftId: addonDigitalAssetItem.draftId, deleted: false });
									}
									addons.push({
										shipStationItem: addonShipStationItem,
										digitalAssetItem: addonDigitalAssetItem,
										isDigital: addonIsDigital,
										stripeItem: {
											counterpartIds: counterpartIds.concat(
												addonItem.counterparts
													? addonItem.counterparts.map(cpItem => {
															return { id: cpItem.id, draftId: "", deleted: false };
													  })
													: []
											),
											deleted: false,
											draftId: "",
											duration: "",
											id: addonItem.id,
											planId: addonStripeData.planId,
											price: addonStripeData.price,
											name: addonStripeData.name!
										},
										mainImageKey: addonItem?.mainImageKey as string,
										mainImageUrl: addonItem?.mainImageUrl as string,
										isDefault: addonStripeData?.isDefault as boolean
									});
									dataAddons.push({
										stripePlanId: addonStripeData.planId,
										digitalAssetProductKey: addonDigitalAssetItem.productKey,
										shipStationProductSKU: addonShipStationItem.productSKU,
										mainImageKey: addonItem?.mainImageKey as string,
										mainImageUrl: addonItem?.mainImageUrl as string
									});
								});
							}
							dispatch(setStateValue({ key: "data.externalIntegrationData.initial.addons", value: dataAddons }));
							ustFormInputValues.initial.addons = addons;
						}
						ustFormInputValues[ustItemType] = {
							...ustFormInputValues[ustItemType],
							...commonData
						};
						if (mainPlanId) {
							dispatch(
								setStateValue({
									key: `data.externalIntegrationData.${ustItemType}.stripePlanId`,
									value: mainPlanId
								})
							);
							dispatch(fetchStripeData(mainPlanId, ustItemType));
						}
					});
				dispatch(
					populateInputs({
						inputsStatePath: "userSubscriptionTypesForm.inputs",
						templatesStatePath: "userSubscriptionTypesForm.templates",
						values: ustFormInputValues as unknown as Record<string, unknown>
					})
				);
				for (const index in ustFormInputValues.initial.addons) {
					const addon = ustFormInputValues.initial.addons[index];
					const path = `initial.addons.${index}`;
					if (addon.isDigital) {
						dispatch(
							setStateValue({
								key: `userSubscriptionTypesForm.inputs.${path}.digitalAssetItem.productKey.name`,
								value: addon.digitalAssetItem.name
							})
						);
						dispatch(toggleDigitalAsset(true, path));
					}
				}
				dispatch(setStateValue({ key: "externalIntegrationData", value: externalIntegrationData }));
				dispatch(validateForm({ formStatePath: "userSubscriptionTypesForm" }));
			}
			dispatch(setStateValue({ key: "newCourseId", value: undefined }));
			if (!form || form === "courseFeaturesForm") {
				const courseFeaturesFormInputValues = {
					additionalInfo: courseData?.data?.additionalInformation
				};
				dispatch(
					populateInputs({
						inputsStatePath: "courseFeaturesForm.inputs",
						values: courseFeaturesFormInputValues as unknown as Record<string, unknown>
					})
				);
			}
		} catch (e) {
			console.error(e);
			dispatch(emit({ message: "Error fetching the course data.", color: "error", preventAutoDismiss: true }));
		} finally {
			dispatch(setStateValue({ key: stateLoaderKey, value: false }));
		}
	};
};

export const toggleDigitalAsset = (isDigitalRequired: boolean, innerPath: string): AppThunk => {
	return async dispatch => {
		const digitalAssetPath = `userSubscriptionTypesForm.inputs.${innerPath}.digitalAssetItem`;
		const shipStationPath = `userSubscriptionTypesForm.inputs.${innerPath}.shipStationItem`;
		dispatch(
			setStateValue({
				key: `${digitalAssetPath}.productKey.validations.required`,
				value: isDigitalRequired
			})
		);
		dispatch(setStateValue({ key: `${shipStationPath}.productSKU.validations.required`, value: !isDigitalRequired }));
	};
};

export const setDigitalAssetName = (file: File, innerPath: string): AppThunk => {
	return async dispatch => {
		const digitalAssetPath = `userSubscriptionTypesForm.inputs.${innerPath}.digitalAssetItem`;
		dispatch(
			setStateValue({
				key: `${digitalAssetPath}.name.value`,
				value: file.name
			})
		);
	};
};

export const fetchShipStationData = (productSKU: string, innerPath: string): AppThunk => {
	return async (dispatch, getState) => {
		if (!productSKU) {
			return;
		}
		const basePath = `userSubscriptionTypesForm.inputs.${innerPath}.shipStationItem`;
		try {
			await new Promise<void>(resolve => {
				setTimeout(() => resolve(), 500);
			});
			const { courseAddEdit: state } = getState();
			const { value: inputValue } = get(state, `${basePath}.productSKU`);
			if (inputValue !== productSKU) {
				return;
			}
			dispatch(setStateValue({ key: `${basePath}.name.value`, value: "" }));
			dispatch(setStateValue({ key: `${basePath}.isLoading.value`, value: true }));
			const shipStationData = await userSubscriptionTypesService.getStockKeepingProductData(
				ExternalIntegrationIds.ShipStation,
				productSKU
			);
			dispatch(setStateValue({ key: `${basePath}.name.value`, value: shipStationData.name }));
		} catch (e) {
			const err = e as { message: string };
			if (err && err.message) {
				dispatch(_emit(err.message, "error"));
				return;
			}
			dispatch(_emit("An error has occurred while retrieving data from Acutrack.", "error"));
		} finally {
			dispatch(setStateValue({ key: `${basePath}.isLoading.value`, value: false }));
		}
	};
};

export const fetchStripeData = (planId: string, innerPath: string): AppThunk => {
	return async (dispatch, getState) => {
		if (!planId) {
			return;
		}
		const basePath = `userSubscriptionTypesForm.inputs.${innerPath}.stripeItem`;
		try {
			await new Promise<void>(resolve => {
				setTimeout(() => resolve(), 500);
			});
			const { courseAddEdit: state } = getState();
			const { externalIntegrationData } = state;
			const { value: inputValue } = get(state, `${basePath}.planId`);
			if (inputValue !== planId) {
				return;
			}
			const isAddonItem = innerPath.match(/addons/);
			dispatch(setStateValue({ key: `${basePath}.isLoading.value`, value: true }));
			///Reset the values before get call
			dispatch(setStateValue({ key: `${basePath}.name.value`, value: "" }));
			dispatch(setStateValue({ key: `${basePath}.price.value`, value: 0 }));
			dispatch(
				setStateValue({
					key: `${basePath}.duration.value`,
					value: undefined
				})
			);
			const stripeAPIData = await userSubscriptionTypesService.getPaymentProviderPriceData(
				ExternalIntegrationIds.Stripe,
				planId
			);
			if (PAYMENT_CONSTANTS.RECURRING === innerPath && stripeAPIData.type === PAYMENT_CONSTANTS.ONE_TIME) {
				dispatch(_emit("Invalid entry for recurring subscription", "error"));
				return;
			}
			if (PAYMENT_CONSTANTS.INITIAL === innerPath && stripeAPIData.type === PAYMENT_CONSTANTS.ONE_TIME) {
				dispatch(_emit("Invalid entry for initial subscription", "error"));
				return;
			}
			if (isAddonItem && stripeAPIData.type !== PAYMENT_CONSTANTS.ONE_TIME) {
				dispatch(_emit("Addons should be one time payment", "error"));
				return;
			}
			dispatch(setStateValue({ key: `${basePath}.name.value`, value: stripeAPIData.name }));
			dispatch(setStateValue({ key: `${basePath}.price.value`, value: stripeAPIData.price }));
			if (stripeAPIData.duration) {
				dispatch(
					setStateValue({
						key: `${basePath}.duration.value`,
						value: stripeAPIData.duration
					})
				);
			}
			if (!isAddonItem) {
				if (!externalIntegrationData.currency || !externalIntegrationData.currency.length) {
					dispatch(
						setStateValue({
							key: "externalIntegrationData.currency",
							value: STRIPE_CURRENCY_SYMBOL_MAP[stripeAPIData.currency]
						})
					);
				}
				if (!externalIntegrationData.planName || !externalIntegrationData.planName.length) {
					dispatch(setStateValue({ key: "externalIntegrationData.planName", value: stripeAPIData.planName }));
				}
			}
			dispatch(setStateValue({ key: `${basePath}.isLoading.value`, value: false }));
		} catch (e) {
			const err = e as { message: string };
			if (err && err.message) {
				dispatch(_emit(err.message, "error"));
				return;
			}
			dispatch(_emit("An error has occurred while retrieving data from Stripe.", "error"));
		} finally {
			dispatch(setStateValue({ key: `${basePath}.isLoading.value`, value: false }));
		}
	};
};

export function getFullState(state: RootState): CourseAddEditState {
	return state.courseAddEdit;
}

export function getStateValue<ReturnType>(state: RootState, key: string): ReturnType {
	return utilsGetStateValue<CourseAddEditState, ReturnType>(state.courseAddEdit, key);
}

export const saveForm = (form?: string): AppThunk => {
	return async (dispatch, getState) => {
		try {
			dispatch(setStateValue({ key: "saveIsLoading", value: true }));
			const { courseAddEdit: state } = getState();
			const { data: stateCourseData, externalIntegrationData } = state;
			let body: CourseCreateDto | CourseUpdateDataDto | null = null;
			let courseData = { ...stateCourseData };
			if (!form || form === "courseForm") {
				dispatch(validateForm({ formStatePath: "courseForm", markInputsAsDirty: true }));
				const { courseForm } = getState().courseAddEdit;
				if (!courseForm.valid) {
					dispatch(_emit("Please fill in all fields correctly and try again.", "error"));
					return;
				}
				const { rawData: courseFormData } = courseForm;
				const totalLength =
					parseInt(courseFormData.timeLengthHours as unknown as string, 10) * 60 +
					parseInt(courseFormData.timeLengthMinutes as unknown as string, 10);
				body = {
					...courseFormData,
					endOfCourseSummary: courseFormData.name,
					manualTimeLengthComponent: courseData.manualTimeLengthComponent,
					totalTimeLengthInMinutes: courseData.totalTimeLengthInMinutes,
					userSubscriptionTypes: [],
					allowedForUserTypeIds: [{ value: UserTypes.RegularStudent }]
				};
				courseData = { ...courseData, ...courseFormData };
				if (courseFormData.mainImageKey) {
					body.mainImageKey = courseFormData.mainImageKey;
				}
				if (courseFormData.calculateTimeLengthAutomatically) {
					body.manualTimeLengthComponent = totalLength;
				} else {
					body.totalTimeLengthInMinutes = totalLength;
				}
			} else {
				body = {
					name: courseData.name,
					endOfCourseSummary: courseData.name,
					isActive: courseData.isActive,
					calculateTimeLengthAutomatically: courseData.calculateTimeLengthAutomatically,
					manualTimeLengthComponent: courseData.manualTimeLengthComponent,
					totalTimeLengthInMinutes: courseData.totalTimeLengthInMinutes,
					userSubscriptionTypes: [],
					allowedForUserTypeIds: [{ value: UserTypes.RegularStudent }]
				};
			}
			if (!form || form === "userSubscriptionTypesForm") {
				dispatch(validateForm({ formStatePath: "userSubscriptionTypesForm", markInputsAsDirty: true }));
				const { userSubscriptionTypesForm } = getState().courseAddEdit;
				if (!userSubscriptionTypesForm.valid) {
					dispatch(_emit("Please fill in all fields correctly and try again.", "error"));
					return;
				}
				const { initial: originalInitialExternalIntegrationData, recurring: originalRecurringExternalIntegrationData } =
					stateCourseData.externalIntegrationData;
				if (userSubscriptionTypesForm.valid) {
					const { initial, recurring, trial, hasTrial } = cloneDeep(userSubscriptionTypesForm.rawData);
					body.hasTrial = hasTrial;
					const {
						shipStationItem: initialShipStationItem,
						digitalAssetItem: initialDigitalAssetItem,
						isDigital: initialIsDigital,
						stripeItem: initialStripeItem
					} = initial;

					const { stripeItem: recurringStripeItem } = recurring;
					const addons: UserSubscriptionTypeExternalIntegrationDataItemUpsertDto[] = [];
					const assetTypeChanged = initialIsDigital !== originalInitialExternalIntegrationData.isDigital;
					const shipStationSkuUpdated =
						(assetTypeChanged && !originalInitialExternalIntegrationData.shipStationProductSKU) ||
						initialShipStationItem.productSKU !== originalInitialExternalIntegrationData.shipStationProductSKU;
					const digitalAssetKeyUpdated =
						(assetTypeChanged && !originalInitialExternalIntegrationData.digitalAssetProductKey) ||
						initialDigitalAssetItem.productKey !== originalInitialExternalIntegrationData.digitalAssetProductKey;

					const addRecurring =
						!originalRecurringExternalIntegrationData.stripePlanId ||
						recurringStripeItem.planId !== originalRecurringExternalIntegrationData.stripePlanId;
					let addTrial = false;
					const { price } = initialStripeItem;
					const { price: recuringPrice } = recurringStripeItem;

					if (!price) {
						dispatch(_emit("Intial price should not be zero", "error"));
						return;
					}
					if (!recuringPrice) {
						dispatch(_emit("Recurring charge should not be zero", "error"));
						return;
					}
					if (!initialIsDigital && !initialShipStationItem.name) {
						dispatch(_emit("Initial Acutrack Product Name should not be empty", "error"));
						return;
					}
					if (initialIsDigital && !initialDigitalAssetItem.productKey) {
						dispatch(_emit("Initial Digital Item should not be empty", "error"));
						return;
					}
					if (addRecurring) {
						initial.nextSubscriptionTypeDraftId = recurring.draftId;
					} else {
						initial.nextSubscriptionTypeId = recurring.id;
					}

					initial.name = `Course ${courseData.name} - ${externalIntegrationData.planName}`;
					initial.description = initial.name;
					recurring.name = `Course ${courseData.name} - ${externalIntegrationData.planName} (Recurring)`;
					recurring.description = recurring.name;
					trial.name = `Course ${courseData.name} - ${externalIntegrationData.planName} (Trial)`;
					trial.description = trial.name;
					if (initial.addons) {
						for (let index = 0; index < initial.addons.length; index++) {
							const addonItem = initial.addons[index];
							const {
								shipStationItem: addonShipStationItem,
								digitalAssetItem: addonDigitalAssetItem,
								isDigital: addonIsDigital,
								isDefault: addonIsDefault,
								stripeItem: addonStripeItem,
								mainImageKey
							} = addonItem;
							const { price: addonPrice, deleted } = addonStripeItem;
							if (!addonPrice && deleted === false) {
								dispatch(_emit("Addons price should not be zero", "error"));
								return;
							}
							if (!addonIsDigital && !addonShipStationItem.name) {
								dispatch(_emit("Addons Acutrack Product Name should not be empty", "error"));
								return;
							}
							if (addonIsDigital && !addonDigitalAssetItem.productKey) {
								dispatch(_emit("Addons Digital Asset should not be empty", "error"));
								return;
							}
							if (!mainImageKey) {
								dispatch(_emit("Addons image is required", "error"));
								return;
							}
							const digitalAssetAddonItem: UserSubscriptionTypeExternalIntegrationDataItemUpsertDto = {
								draftId: addonDigitalAssetItem.draftId,
								integrationId: ExternalIntegrationIds.DigitalAsset,
								subscriptionTypeId: 0, // this gets overwritten in the back-end
								data: {
									name: addonDigitalAssetItem.name,
									productKey: addonDigitalAssetItem.productKey
								},
								deleted: addonDigitalAssetItem.deleted
							};
							const shipStationApiAddonItem: UserSubscriptionTypeExternalIntegrationDataItemUpsertDto = {
								draftId: addonShipStationItem.draftId,
								integrationId: ExternalIntegrationIds.ShipStation,
								subscriptionTypeId: 0, // this gets overwritten in the back-end
								data: {
									name: addonShipStationItem.name,
									productSKU: addonShipStationItem.productSKU
								},
								deleted: addonShipStationItem.deleted
							};
							const stripeApiAddonItem: UserSubscriptionTypeExternalIntegrationDataItemUpsertDto = {
								draftId: addonStripeItem.draftId,
								integrationId: ExternalIntegrationIds.Stripe,
								subscriptionTypeId: 0, // this gets overwritten in the back-end
								data: { planId: addonStripeItem.planId, isDefault: addonIsDefault },
								counterpartIds: [],
								counterpartDraftIds: [],
								deleted: addonStripeItem.deleted,
								mainImageKey: mainImageKey
							};
							digitalAssetAddonItem.parentDraftId = initialStripeItem.draftId;
							shipStationApiAddonItem.parentDraftId = initialStripeItem.draftId;
							stripeApiAddonItem.parentDraftId = initialStripeItem.draftId;
							if (!addonIsDigital) {
								digitalAssetAddonItem.deleted = true;
								stripeApiAddonItem.counterpartDraftIds!.push(addonShipStationItem.draftId);
							} else {
								shipStationApiAddonItem.deleted = true;
								stripeApiAddonItem.counterpartDraftIds!.push(addonDigitalAssetItem.draftId);
							}

							const { planId } = addonStripeItem;
							if (!planId && !planId?.length) {
								digitalAssetAddonItem.deleted = true;
								shipStationApiAddonItem.deleted = true;
								stripeApiAddonItem.deleted = true;
							}
							addons.push(digitalAssetAddonItem);
							addons.push(shipStationApiAddonItem);
							addons.push(stripeApiAddonItem);
						}
					}
					const addonsUpdate =
						initial.addons?.filter(f => f.stripeItem.deleted === false).length !==
							originalInitialExternalIntegrationData.addons?.length ||
						!initial.addons?.every(ia =>
							originalInitialExternalIntegrationData.addons?.some(
								oa =>
									oa.mainImageKey == ia.mainImageKey &&
									(oa.shipStationProductSKU == ia.shipStationItem.productSKU ||
										oa.digitalAssetProductKey == ia.digitalAssetItem.productKey) &&
									oa.isDigital == ia.isDigital &&
									oa.stripePlanId === ia.stripeItem.planId
							)
						);
					const addInitial =
						digitalAssetKeyUpdated ||
						shipStationSkuUpdated ||
						!originalInitialExternalIntegrationData.stripePlanId ||
						initialStripeItem.planId !== originalInitialExternalIntegrationData.stripePlanId ||
						addonsUpdate;

					if (addInitial) {
						//We are replacing subscription with new one so we add draft id
						trial.nextSubscriptionTypeDraftId = initial.draftId;
					} else {
						// We have an existing initial subscription so we use id
						trial.nextSubscriptionTypeId = initial.id;
					}

					body.userSubscriptionTypes = [];
					if (addInitial) {
						const counterpartIds: { value: number; deleted: boolean }[] = [];
						const counterpartDraftIds: string[] = [];
						if (!shipStationSkuUpdated && initialShipStationItem.id) {
							counterpartIds.push({ value: initialShipStationItem.id, deleted: initialIsDigital });
						} else if (!initialIsDigital) {
							counterpartDraftIds.push(initialShipStationItem.draftId);
						}
						if (!digitalAssetKeyUpdated && initialDigitalAssetItem.id) {
							counterpartIds.push({ value: initialDigitalAssetItem.id, deleted: !initialIsDigital });
						} else if (initialIsDigital) {
							counterpartDraftIds.push(initialDigitalAssetItem.draftId);
						}
						// Mark original subscription as inactive
						if (initial.id) {
							body.userSubscriptionTypes.push({
								id: initial.id,
								draftId: initial.draftId,
								nextSubscriptionTypeId: initial.nextSubscriptionTypeId,
								nextSubscriptionTypeDraftId: initial.nextSubscriptionTypeDraftId,
								name: initial.name,
								description: initial.description,
								isRecurring: initial.isRecurring,
								isTrial: initial.isTrial,
								isActive: false,
								deleted: !!initial.deleted
							});
						}
						const bookItem = !initialIsDigital
							? {
									id: 0,
									draftId: initialShipStationItem.draftId,
									integrationId: ExternalIntegrationIds.ShipStation,
									subscriptionTypeId: 0,
									data: {
										name: initialShipStationItem.name,
										productSKU: initialShipStationItem.productSKU
									},
									deleted: initialShipStationItem.deleted
							  }
							: {
									id: 0,
									draftId: initialDigitalAssetItem.draftId,
									integrationId: ExternalIntegrationIds.DigitalAsset,
									subscriptionTypeId: 0,
									data: {
										name: initialDigitalAssetItem.name,
										productKey: initialDigitalAssetItem.productKey
									},
									deleted: initialDigitalAssetItem.deleted
							  };
						// Add new subscription
						body.userSubscriptionTypes.push({
							id: 0,
							draftId: initial.draftId,
							nextSubscriptionTypeId: initial.nextSubscriptionTypeId,
							nextSubscriptionTypeDraftId: initial.nextSubscriptionTypeDraftId,
							name: initial.name,
							description: initial.description,
							isRecurring: initial.isRecurring,
							isTrial: initial.isTrial,
							isActive: true,
							deleted: !!initial.deleted,
							externalIntegrationDataItems: [
								bookItem,
								{
									id: 0,
									draftId: initialStripeItem.draftId,
									integrationId: ExternalIntegrationIds.Stripe,
									subscriptionTypeId: 0,
									counterpartIds,
									counterpartDraftIds,
									data: { planId: initialStripeItem.planId }
								},
								...addons.filter(f => f.deleted === false)
							]
						});
					} else if (addRecurring) {
						//Update nextSubscriptionTypeId
						body.userSubscriptionTypes.push({
							id: initial.id,
							draftId: initial.draftId,
							nextSubscriptionTypeId: initial.nextSubscriptionTypeId,
							nextSubscriptionTypeDraftId: initial.nextSubscriptionTypeDraftId,
							name: initial.name,
							description: initial.description,
							isRecurring: initial.isRecurring,
							isTrial: initial.isTrial,
							isActive: initial.isActive,
							deleted: !!initial.deleted
						});
					}
					if (addRecurring) {
						// Mark original subscription as inactive
						if (!!recurring.id) {
							body.userSubscriptionTypes.push({
								id: recurring.id,
								draftId: recurring.draftId,
								nextSubscriptionTypeId: recurring.nextSubscriptionTypeId,
								nextSubscriptionTypeDraftId: recurring.nextSubscriptionTypeDraftId,
								name: recurring.name,
								description: recurring.description,
								isRecurring: recurring.isRecurring,
								isTrial: recurring.isTrial,
								isActive: false,
								deleted: !!recurring.deleted
							});
						}
						// Add new subscription
						body.userSubscriptionTypes.push({
							id: 0,
							draftId: recurring.draftId,
							nextSubscriptionTypeId: recurring.nextSubscriptionTypeId,
							nextSubscriptionTypeDraftId: recurring.nextSubscriptionTypeDraftId,
							name: recurring.name,
							description: recurring.description,
							isRecurring: recurring.isRecurring,
							isTrial: recurring.isTrial,
							isActive: true,
							deleted: !!recurring.deleted,
							externalIntegrationDataItems: [
								{
									...recurringStripeItem,
									id: 0,
									integrationId: ExternalIntegrationIds.Stripe,
									subscriptionTypeId: 0,
									counterpartIds: [],
									counterpartDraftIds: [],
									data: { planId: recurringStripeItem.planId }
								}
							]
						});
					}
					if (!hasTrial) {
						if (trial.id) {
							addTrial = true;
							trial.isActive = false;
						} else {
							addTrial = false;
						}
					} else {
						addTrial = !(trial.id && !addInitial);
					}
					if (addTrial) {
						body.userSubscriptionTypes.push({
							id: trial.id,
							draftId: trial.draftId,
							nextSubscriptionTypeId: trial.nextSubscriptionTypeId,
							nextSubscriptionTypeDraftId: trial.nextSubscriptionTypeDraftId,
							name: trial.name,
							description: trial.description,
							isRecurring: trial.isRecurring,
							isTrial: trial.isTrial,
							isActive: trial.isActive,
							deleted: !!trial.deleted
						});
					}
				}
			}
			if (!form || form === "courseFeaturesForm") {
				dispatch(validateForm({ formStatePath: "courseFeaturesForm", markInputsAsDirty: true }));
				const { courseFeaturesForm } = getState().courseAddEdit;
				const { marketingPoints } = getState().courseAddEdit.data.data;
				if (!courseFeaturesForm.valid) {
					dispatch(_emit("Please fill in all of the course fetures fields correctly and try again.", "error"));
					return;
				} else {
					const { rawData: courseFeaturesFormData } = courseFeaturesForm;
					body = {
						data: {
							additionalInformation: courseFeaturesFormData.additionalInfo,
							marketingPoints: marketingPoints
						}
					};
				}
			}
			if (!courseData.id) {
				const newCourse = await coursesService.create(body as CourseCreateDto);
				dispatch(setStateValue({ key: "newCourseId", value: newCourse.id }));
				dispatch(_emit("Course created successfully.", "success"));
				return;
			}
			await coursesService.update({
				filters: { id: courseData.id },
				data: body as CourseUpdateDataDto
			});
			dispatch(fetchData(courseData.id, { stateLoaderKey: "saveIsLoading" }));
			dispatch(_emit("Course updated successfully.", "success"));
		} catch (e) {
			console.error(e);
			dispatch(_emit("An error has occurred while saving your data.", "error"));
		} finally {
			dispatch(setStateValue({ key: "saveIsLoading", value: false }));
		}
	};
};

export const updatePublishedState = (): AppThunk => {
	return async (dispatch, getState) => {
		try {
			dispatch(setStateValue({ key: "publishIsLoading", value: true }));
			const { courseAddEdit: state } = getState();
			const { courseForm, data: course } = state;
			const { raw } = await coursesService.update({
				filters: { id: course.id },
				data: { isActive: !courseForm.inputs.isActive.value } as CourseUpdateDataDto
			});
			const isActive = raw[0].isActive;
			dispatch(setStateValue({ key: "data.isActive", value: isActive }));
			dispatch(setStateValue({ key: "courseForm.inputs.isActive.value", value: isActive }));
			dispatch(setStateValue({ key: "publishIsLoading", value: false }));
			dispatch(_emit(`Course ${isActive ? "published" : "unpublished"} successfully.`, "success"));
		} catch (e) {
			console.error(e);
			dispatch(_emit("An error has occurred while saving your data.", "error"));
		}
	};
};

export default courseAddEditSlice.reducer;
